import { Injectable, signal, computed } from '@angular/core';

/**
 * RESPONSIBILITY
 * Manages the user's registered vehicles (registration plate, make/model,
 * EV flag, default vehicle) with localStorage persistence. Follows the
 * UserPreferencesService pattern.
 *
 * SIGNALS
 * - vehicles: Signal<Vehicle[]> — all registered vehicles, default first
 * - defaultVehicle: Signal<Vehicle | null> — the vehicle used for parking bookings
 * - hasVehicles: Signal<boolean> — at least one vehicle registered
 * - electricCount: Signal<number> — number of registered EVs
 *
 * USED BY
 * - ProfileVehiclesPageComponent (register / edit / remove vehicles)
 * - ProfileCompletionService (vehicle step of the completion tracker)
 * - ProfileOverviewPageComponent (key-area summary line)
 *
 * STORAGE
 * - localStorage key: 'im-vehicles' (offline-first cache)
 * - Backend: ProfileSettings.vehicles (myProfileSettings /
 *   updateProfileSettings, replace-all semantics) — hydrated + write-through
 *   by ProfileSettingsSyncService, so the fleet follows the user across
 *   devices and is cleared locally on sign-out.
 * - Gracefully handles corruption and quota-exceeded errors
 *
 * AI CONTEXT
 * - This service makes NO API calls itself — ProfileSettingsSyncService owns
 *   the round-trips.
 * - Registration plates are personal data: never put the plate text in
 *   analytics payloads and mask the input with `class="ph-mask"`.
 * - Exactly one vehicle is default whenever the list is non-empty (the
 *   parking flow books against the default vehicle).
 * - The EV flag powers EV-bay matching (see "Match against employees with
 *   EV vehicle profiles" in the backend amenity hints).
 */

export type VehicleType = 'car' | 'van' | 'motorcycle';

export interface Vehicle {
  id: string;
  /** Registration plate, normalised to uppercase (e.g. "AB12 CDE"). */
  registration: string;
  make: string;
  model: string;
  /** One of VEHICLE_COLOURS ids, or null when not set. */
  colour: string | null;
  type: VehicleType;
  isElectric: boolean;
  isDefault: boolean;
  /** ISO timestamp of registration (device time). */
  addedAt: string;
}

/** Input for add/update — id/isDefault/addedAt are managed by the service. */
export type VehicleInput = Omit<Vehicle, 'id' | 'isDefault' | 'addedAt'>;

/**
 * Real-world vehicle paint colours (hardcoded, NOT themed — like the dietary
 * tag colours, the meaning is tied to the colour itself: a red car stays red
 * in every theme). Used only for the small swatch on vehicle cards.
 */
export const VEHICLE_COLOURS: readonly { id: string; hex: string }[] = [
  { id: 'white', hex: '#e8e8e8' },
  { id: 'silver', hex: '#b8bcc2' },
  { id: 'grey', hex: '#6b7078' },
  { id: 'black', hex: '#1c1e22' },
  { id: 'blue', hex: '#2563eb' },
  { id: 'red', hex: '#dc2626' },
  { id: 'green', hex: '#15803d' },
  { id: 'orange', hex: '#ea580c' },
] as const;

export function vehicleColourHex(id: string | null): string | null {
  return VEHICLE_COLOURS.find(c => c.id === id)?.hex ?? null;
}

const STORAGE_KEY = 'im-vehicles';

@Injectable({ providedIn: 'root' })
export class VehiclesService {
  private readonly _vehicles = signal<Vehicle[]>(this.load());

  /** All registered vehicles — default vehicle first, then by addedAt. */
  readonly vehicles = this._vehicles.asReadonly();

  readonly defaultVehicle = computed(() => this._vehicles().find(v => v.isDefault) ?? null);
  readonly hasVehicles = computed(() => this._vehicles().length > 0);
  readonly electricCount = computed(() => this._vehicles().filter(v => v.isElectric).length);

  /** Register a new vehicle. The first vehicle automatically becomes default. */
  add(input: VehicleInput): Vehicle {
    const vehicle: Vehicle = {
      ...this.normalise(input),
      id: this.newId(),
      isDefault: this._vehicles().length === 0,
      addedAt: new Date().toISOString(),
    };
    this.persist([...this._vehicles(), vehicle]);
    return vehicle;
  }

  /** Update an existing vehicle's editable fields. */
  update(id: string, input: VehicleInput): void {
    this.persist(this._vehicles().map(v =>
      v.id === id ? { ...v, ...this.normalise(input) } : v,
    ));
  }

  /** Remove a vehicle. If it was the default, the oldest remaining one is promoted. */
  remove(id: string): void {
    const remaining = this._vehicles().filter(v => v.id !== id);
    if (remaining.length > 0 && !remaining.some(v => v.isDefault)) {
      remaining[0] = { ...remaining[0], isDefault: true };
    }
    this.persist(remaining);
  }

  /** Make the given vehicle the default one used for parking bookings. */
  setDefault(id: string): void {
    this.persist(this._vehicles().map(v => ({ ...v, isDefault: v.id === id })));
  }

  /**
   * Replace the whole fleet (cross-device hydration from the backend).
   * Ensures exactly one default when the list is non-empty.
   */
  replaceAll(vehicles: Vehicle[]): void {
    const next = vehicles.map(v => ({ ...v }));
    if (next.length > 0 && !next.some(v => v.isDefault)) {
      next[0] = { ...next[0], isDefault: true };
    }
    this.persist(next);
  }

  /** Reset to an empty fleet (sign-out on shared devices). */
  reset(): void {
    this.persist([]);
  }

  /** Get a snapshot of current vehicles. */
  snapshot(): Vehicle[] {
    return this._vehicles().map(v => ({ ...v }));
  }

  // ── Internals ──────────────────────────────────────────

  private normalise(input: VehicleInput): VehicleInput {
    return {
      ...input,
      registration: input.registration.trim().toUpperCase(),
      make: input.make.trim(),
      model: input.model.trim(),
    };
  }

  private newId(): string {
    try {
      return crypto.randomUUID();
    } catch {
      return `veh-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    }
  }

  private persist(vehicles: Vehicle[]): void {
    const sorted = [...vehicles].sort((a, b) => {
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
      return a.addedAt.localeCompare(b.addedAt);
    });
    this._vehicles.set(sorted);
    this.save(sorted);
  }

  private load(): Vehicle[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const stored: unknown = JSON.parse(raw);
        if (Array.isArray(stored)) {
          return stored.filter((v): v is Vehicle =>
            !!v && typeof v === 'object'
            && typeof (v as Vehicle).id === 'string'
            && typeof (v as Vehicle).registration === 'string',
          );
        }
      }
    } catch {
      /* corrupt data — start empty */
    }
    return [];
  }

  private save(vehicles: Vehicle[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(vehicles));
    } catch {
      /* storage full — ignore */
    }
  }
}
